/* The client detail page's rules: what a counter means, and what "trained" is.
 *
 * `EVERFIT-MAP.md` §2 mapped the competitor's client page, and the one small idea with teeth is
 * that **every training counter is completed-versus-ASSIGNED, never a bare count** — `LAST 7
 * DAYS 0/0`, `LAST 30 DAYS 1/2`. A bare "3 workouts this week" cannot tell a coach whether that
 * is all of them or half of them, which is the only thing the number is for.
 *
 * PURE on purpose — no Supabase, no React — so the rules are EXECUTED (`client.test.ts`) rather
 * than reasoned about. The Swift harness cannot reach TypeScript, so a `vitest` file is the only
 * place a rule in this app can be given teeth.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════
 * 🔴 THE MEASUREMENT THAT DECIDED THIS FILE, AND IT CONTRADICTED SHIPPED CODE.
 *
 * "Did this person train?" has THREE answers in this database and they disagree wildly. Measured
 * live on 2026-08-28, through RLS as the coach, over his two real clients:
 *
 *   | signal                                   | Yiannis | Anastasia |
 *   |------------------------------------------|---------|-----------|
 *   | `workout_sessions.completed_at` is set    |       7 |     **0** |
 *   | `workout_sessions.status = 'completed'`   |      52 |     **0** |
 *   | **a set was actually logged**             |  7 days | **2 days**|
 *
 * `Roster.tsx` shipped reading the FIRST one, and it therefore told the coach that Anastasia
 * **"Has never trained — 4 workouts assigned, none started."** Both halves are false: she logged
 * **22 sets on 2026-08-11 and 22 more on 2026-08-15**, twenty exercises each time. She is the
 * exact case §11's job 7 exists for — *someone has quietly stopped training* — and the narrow
 * signal relabelled her as someone who never began, which is a different conversation with a
 * different first sentence.
 *
 * WHY the three disagree, rather than which to prefer as a matter of taste:
 *   - `completed_at` is written **only by the Finish button**, which `CLAUDE.md` records was
 *     unreachable until 2026-08-24. It means "pressed Finish", not "trained".
 *   - `status='completed'` is written by a different path — 45 of the 52 rows carry no
 *     `completed_at` at all.
 *   - An **exercise row** is no better and is worse than it looks: **84 sessions have exercise
 *     rows and zero logged sets**, because the player seeds the whole prescription the moment a
 *     workout is opened. "Opened it" is not "did it".
 *
 * So the signal is **a logged set**, and nothing else is. `set_logs` carries both `user_id` and
 * `completed_at`, so it needs no join, and — the half that settles it — **73 of the sets belong
 * to sessions whose `scheduled_date` is NULL**, so a date derived from the session would simply
 * lose six days of somebody's training.
 * ══════════════════════════════════════════════════════════════════════════════════════════
 */

import { lastTrainedLine } from "./appRoster";
import { dayKey, dayKeyOf, daysSince, shiftDayKey, trainedDayKey } from "./days";
import { rawSetOf } from "./rosterData";
// The one date spelling the web shows a coach — imported, never a second formatter. A
// headline naming a day must name it exactly as the week strip under it does.
import { shortDate } from "./weekLine";
import { isOptionalTask, isTaskAssignment, type AssignmentEmbed } from "./roster";
// The silence threshold is the ROSTER's rule and is imported rather than retyped. Two screens
// disagreeing about when somebody has gone quiet is the same defect as two palettes.
import { goneQuietAfterDays } from "./roster";

/** One logged set, reduced to what this page needs: whose it was, and when. */
/** A logged set and — W112 — its session's `scheduled_date` (the day it counts on; null = the session has none). */
export type LoggedSet = { userId: string | null; completedAtISO: string | null; scheduledDateISO?: string | null };

/** One thing the coach asked for. */
export type Assignment = {
  id: string;
  /** `coach_assignments.scheduled_date` — a bare date, and nullable in the schema. */
  scheduledDateISO: string | null;
  workoutName: string | null;
  /** A hidden assignment never reached the client. It may never be counted against them. */
  hiddenFromClient: boolean;
  /**
   * Whether this assignment is a TASK rather than a workout — `coach_workouts.kind = 'task'`.
   *
   * ⚠️ IT IS ON THE TYPE SO EVERY READER GETS IT, not computed at each site. A daily wrist series
   * is an assignment and is not training; counting it as training makes a diligent athlete look
   * non-compliant, and it did — `19 / 32` on this page against `19 / 21` on the roster, for one
   * man on one day, because only one of the two screens had learned the rule.
   */
  isTask: boolean;
  /**
   * 🔴 OFFERED, NEVER OWED — `coach_workouts.task_optional` (his 2026-10-07 word). On the type for
   * the same reason `isTask` is: every reader gets the one answer (`isOptionalTask`), and the
   * calendar draws it as a word, never a tick.
   */
  isOptional: boolean;
  /**
   * `coach_assignments.note`.
   *
   * ⚠️ IT HAD TWO WRITERS AND NO READER UNTIL SESSION 11, MEASURED. The phone's assign sheet
   * writes it, `AssignPanel` writes it, and nothing in either app rendered it — the athlete's
   * session description comes from `workout.summary` in `AssignedWorkoutBridge`, which never
   * touches this column. Live: **0 of 12 assignments carry one**, so nothing had been lost yet.
   * The Training tab is its first reader. Giving the ATHLETE one is phone work and is queued.
   */
  note: string | null;
  /** W137 (R7) — `coach_assignments.day_position`, the order among the client's assignments on the same day (0 first). Absent
   *  on a row read without it = 0, the column's own default — so an old read sorts exactly as it always did. */
  dayPosition?: number;
  /** `coach_assignments.assigned_at` — the tie-break after `dayPosition` (R7's order). */
  assignedAtISO?: string | null;
};

/**
 * The days this person actually trained, as `YYYY-MM-DD` keys.
 *
 * A SET, not a count, and the difference is not cosmetic: **2026-08-27 alone carries seventeen
 * `workout_sessions` rows for one client**, one with 46 logged sets and sixteen ghosts. Counting
 * sessions would report that day as seventeen training sessions. The day is the unit a coach
 * thinks in and the only one this data can support.
 */
export function trainingDays(sets: LoggedSet[], userId: string): Set<string> {
  const days = new Set<string>();
  for (const s of sets) {
    if (s.userId !== userId || s.completedAtISO === null) continue;
    // W112 — the session's SCHEDULED day, the one the athlete's own phone ticks; an unscheduled set reads its instant.
    const key = trainedDayKey(s.scheduledDateISO, s.completedAtISO);
    if (key !== null) days.add(key);
  }
  return days;
}

/** Sets per trained day (W112's day rule) — the client page's feed and week strip read the days `trainingDays` reads. */
export function setsPerTrainedDay(sets: readonly LoggedSet[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const s of sets) {
    const k = trainedDayKey(s.scheduledDateISO, s.completedAtISO);
    if (k !== null) m.set(k, (m.get(k) ?? 0) + 1);
  }
  return m;
}

/** The most recent day they trained, or null if this data holds none. */
export function lastTrainingDay(days: Set<string>): string | null {
  let latest: string | null = null;
  for (const d of days) if (latest === null || d > latest) latest = d;
  return latest;
}

/**
 * What became of one assignment.
 *
 * ⚠️ `dueToday` IS A CASE OF ITS OWN AND COLLAPSING IT INTO `missed` WOULD FIRE EVERY SINGLE DAY
 * FOR EVERY CLIENT. An assignment scheduled for today has not been missed at 9am. The naive
 * `scheduledDate < now` test is true from one minute past midnight, so it would accuse every
 * client of missing today's session every morning — the loudest possible false positive, on the
 * screen whose entire job is telling a coach who genuinely needs them.
 *
 * ⚠️ AND `notShown` IS NOT `missed`. `coach_assignments.hidden_from_client` is `false` in the
 * client's own read policy, so a hidden row is literally invisible to them; two of this coach's
 * twelve assignments are hidden. Counting one as missed accuses somebody of skipping work they
 * were never shown.
 */
export type Outcome = "trained" | "missed" | "dueToday" | "upcoming" | "notShown" | "undated" | "task";

export function outcome(a: Assignment, days: Set<string>, today: Date): Outcome {
  if (a.hiddenFromClient) return "notShown";
  /* 🔴 A TASK IS NEVER "NOT TRAINED", AND THE OLD ANSWER WAS VISIBLE ON HIS OWN WEEK. His daily
     wrist series is an assignment, so a REST DAY carrying only that task got a red "not trained"
     ring and counted in "5 assigned days not trained". Two of the five were rest days he had
     nothing to do on.
     ⚠️ IT IS TESTED FIRST, BEFORE `days.has(key)`, ON PURPOSE. A task performed on a day he also
     trained would otherwise read "trained" and quietly re-enter the training counters through the
     back door — the outcome has to be about WHAT THE ROW IS before it is about what happened. */
  if (a.isTask) return "task";
  if (a.scheduledDateISO === null) return "undated";
  const key = dayKey(a.scheduledDateISO);
  if (key === null) return "undated";
  if (days.has(key)) return "trained";
  const todayKey = dayKeyOf(today);
  if (key > todayKey) return "upcoming";
  if (key === todayKey) return "dueToday";
  return "missed";
}

/**
 * How each outcome is SAID, in one place.
 *
 * 🔴 IT WAS SAID IN TWO, AND THEY DISAGREED. `Client.tsx` carried a `Record<Outcome, …>` with a
 * distinct mark for all six; `ClientTraining.tsx` carried an inline ternary that knew three of
 * them and rendered every remaining case — `dueToday`, `notShown` AND `undated` — as the same
 * `◆`. So on the Training tab a hidden assignment and a session due today were the same symbol,
 * while the client page's own header told the coach they were different things. This repo's
 * defining bug class with a legend in the place of a vocabulary, between two panels of one
 * screen.
 *
 * A `Record` over the union rather than a function: a seventh outcome fails to COMPILE here
 * instead of falling through to a default nobody notices — which is the same reason `Client.tsx`
 * chose a table over a nested ternary for its tab labels, stated in its own comment.
 *
 * ⚠️ THE MARK IS NOT DECORATION. Selection and status on both surfaces are carried by colour AND
 * by this glyph, because the design system's own rule — and Apple's HIG, which governs the phone
 * this vocabulary is shared with — is that colour alone may never carry information.
 */
export const outcomeText: Record<Outcome, { mark: string; label: string }> = {
  trained: { mark: "\u2713", label: "Trained" },
  missed: { mark: "\u25cf", label: "Not trained" },
  dueToday: { mark: "\u25c6", label: "Due today" },
  upcoming: { mark: "\u2192", label: "Upcoming" },
  notShown: { mark: "\u25cc", label: "Hidden from client" },
  // A task is reported, never scored: it has no "missed" and no "trained", because a coach does
  // not chase a wrist series the way they chase a session. Hollow dot, the quietest mark here.
  task: { mark: "\u25e6", label: "Task" },
  undated: { mark: "?", label: "No date" },
};

/**
 * A completed-versus-assigned counter over a window, which is §2's whole idea.
 *
 * ⚠️ THE NUMERATOR IS TRAINING DAYS IN THE WINDOW, NOT MATCHED ASSIGNMENTS, AND THAT IS
 * DELIBERATE. Matching each assignment to work logged on its own date is available and is a
 * strictly narrower claim: it silently discards a session done a day late, and it discards
 * training on a day nothing was assigned. Both are real — this coach's client trained on
 * 2026-08-17 and 2026-08-20 with no assignment on either day — and a coach reading "0 of 4"
 * about somebody who trained twice would be told the opposite of the truth.
 *
 * So the two numbers answer two honest questions that stand side by side: **how many days did
 * you ask for, and how many days did they train.** `trained` may legitimately exceed `assigned`,
 * and the renderer says so rather than clamping it — a clamp would hide the one athlete doing
 * more than they were told to.
 *
 * ⚠️ `undated` IS COUNTED AND RETURNED SEPARATELY, NEVER DROPPED. An assignment with no date
 * cannot fall inside any window, and silently omitting it makes a denominator that is wrong in
 * the flattering direction. There are zero of them today; the field is nullable, so there is a
 * shape this can meet.
 */
export type WindowCount = {
  assigned: number;
  trained: number;
  /** Hidden rows, excluded from `assigned` and reported so the exclusion is visible. */
  notShown: number;
  /** Dated nowhere, so in no window. Reported for the same reason. */
  undated: number;
  /** Task assignments in the window — assignments, and not training. Reported, never folded in. */
  tasks: number;
};

export function countWindow(
  assignments: Assignment[],
  days: Set<string>,
  fromKey: string,
  toKey: string,
): WindowCount {
  let assigned = 0;
  let notShown = 0;
  let undated = 0;
  let tasks = 0;
  for (const a of assignments) {
    if (a.scheduledDateISO === null) {
      undated += 1;
      continue;
    }
    const key = dayKey(a.scheduledDateISO);
    if (key === null) {
      undated += 1;
      continue;
    }
    if (key < fromKey || key > toKey) continue;
    /* TASKS ARE COUNTED APART, NOT DROPPED. `assigned` is TRAINING assigned — this page printed
       `19 / 32` against the roster's `19 / 21` for one man on one day, because only one of the two
       screens had learned the rule. Reported rather than silently excluded, on the same principle
       as `notShown`: a number that quietly got smaller is a number nobody can check. */
    if (a.isTask) {
      tasks += 1;
      continue;
    }
    if (a.hiddenFromClient) {
      notShown += 1;
      continue;
    }
    assigned += 1;
  }
  let trained = 0;
  for (const d of days) if (d >= fromKey && d <= toKey) trained += 1;
  return { assigned, trained, notShown, undated, tasks };
}

/** The three windows §2 puts at the top of the page, named where they are defined. */
export type Windows = {
  last7: WindowCount;
  last30: WindowCount;
  next7: WindowCount;
};

export function windows(assignments: Assignment[], days: Set<string>, today: Date): Windows {
  const todayKey = dayKeyOf(today);
  return {
    // Inclusive of today: "the last 7 days" to a coach is today and the six before it.
    last7: countWindow(assignments, days, shiftDayKey(today, -6), todayKey),
    last30: countWindow(assignments, days, shiftDayKey(today, -29), todayKey),
    // Strictly ahead — today is already counted in `last7`, and counting it twice would make
    // "nothing assigned for the coming week" false on any day something is due.
    next7: countWindow(assignments, days, shiftDayKey(today, 1), shiftDayKey(today, 7)),
  };
}

/**
 * How to say when they last trained.
 *
 * ⚠️ THE NO-DATA CASE SAYS WHAT WAS LOOKED AT, NOT "NEVER". This is the sentence the roster got
 * wrong, so it is the one place this file refuses to over-claim: a page that has read a bounded
 * window may say "nothing in that window" and may not say "never".
 *
 * 🔴 **THE BUCKETS ARE `lastTrainedLine`'s NOW, WHICH IS THE APP'S OWN (2026-09-19).** This
 * function used to stop at `Trained N days ago` for every age, so a client quiet for nine weeks
 * read *"Trained 63 days ago"* here and *"Trained 9 weeks ago"* on the phone — one product, two
 * vocabularies for one fact, which is this repo's defining bug class. It DELEGATES rather than
 * being replaced because the two callers need two things this page's version still owns: the
 * UNPARSEABLE-date arm (a stored string the app never sees, because the app reads a `Date`) and
 * the null wording, which is the same sentence in both places and is asserted to be so.
 *
 * ⚠️ **THE ONE VISIBLE CHANGE IS AT SEVEN DAYS AND OVER** — `Trained 9 days ago` becomes
 * `Trained last week`. Measured on the live fixture: the client page's desktop still is
 * BYTE-IDENTICAL before and after, because that client trained yesterday; the change is real and
 * simply not reachable by this data.
 */
export function describeLastTraining(lastDayISO: string | null, today: Date): string {
  if (lastDayISO === null) return lastTrainedLine(null);
  const d = daysSince(lastDayISO, today);
  if (d === null) return "Last session has a date this page cannot read";
  return lastTrainedLine(d);
}

/**
 * The one sentence at the top: what, if anything, this coach should do about this person.
 *
 * Ordered by what a coach acts on first, and each arm names a DIFFERENT action rather than
 * scoring one number — B-2's digest precedent and the roster's, held to deliberately. The order
 * matters more than the wording: nothing assigned outranks nothing trained, because a client
 * with nothing to do cannot be accused of not doing it.
 */
export type Standing =
  | { kind: "notJoined"; headline: string; detail: string }
  | { kind: "nothingAssigned"; headline: string; detail: string }
  | { kind: "missingWork"; headline: string; detail: string }
  | { kind: "quiet"; headline: string; detail: string }
  | { kind: "onTrack"; headline: string; detail: string };

/**
 * How far back the overview's "not trained" headline looks.
 *
 * ⚠️ FOUR WEEKS BECAUSE THE REST OF THE BOARD ALREADY SAYS FOUR WEEKS — the body-metrics panel's
 * default window — not because 28 is a good number. One period for one page; a headline and a
 * panel describing different spans of time is the same disagreement in smaller print.
 */
export const missedWindowDays = 28;

/**
 * The headline for missed work — it NAMES what was missed.
 *
 * ⚠️ ONE ROW IS NAMED IN FULL; TWO OR MORE ARE COUNTED AND THE FIRST TWO NAMED — the
 * caller hands them oldest-first (`standing`), so the two named are the two OLDEST. The cut at two
 * is where the sentence stops being readable at a glance rather than at a round number, and the
 * count is said out loud from two up, so the sentence can never imply that the two it names are
 * all there is.
 *
 * ⚠️ THIS PARAGRAPH WAS WRONG FOR AS LONG AS IT EXISTED AND THE CODE WAS RIGHT. It used to
 * read *"TWO ARE NAMED, THREE OR MORE ARE COUNTED"*, which promised no count at two while
 * `rows.length === 1` has always been the only branch without one. Corrected 2026-09-19 on Fable's
 * ruling; the behaviour did not move, and `client.test.ts` pins it at two, at three and at one.
 *
 * ⚠️ AND A ROW WITH NO NAME STILL GETS A SENTENCE. `workoutName` is nullable; `Workout` is what
 * every other reader on this page calls an unnamed one, so this calls it that too rather than
 * inventing a third word or rendering `null · Sat 29 Aug`.
 */
export function missedHeadline(rows: readonly Assignment[]): string {
  const say = (a: Assignment) => {
    const name = (a.workoutName ?? "").trim() === "" ? "Workout" : (a.workoutName ?? "").trim();
    const key = a.scheduledDateISO === null ? null : dayKey(a.scheduledDateISO);
    return key === null ? name : `${name} · ${shortDate(key)}`;
  };
  if (rows.length === 0) return "";
  const first = rows[0];
  if (first === undefined) return "";
  if (rows.length === 1) return `${say(first)} — not trained`;
  const second = rows[1];
  const named = second === undefined ? say(first) : `${say(first)}, ${say(second)}`;
  return `${rows.length} sessions not trained — ${named}`;
}

/* ⚠️ PARKED, NOT LIVE — measured 2026-09-24: no screen reads `standing` since W63 removed the
   Overview's "what needs doing" banner (their page has none). It is KEPT, with `missedHeadline`
   and its tests, because EVERFIT-MAP §6b-ter lists that banner as LOST IN THE COPY, FOR THE
   TOGETHER PHASE TO DECIDE — deleting the rule would take the decision before he does. If that
   phase says no, delete it with its tests (W64). `outcomeText` is parked for the same reason. */
export function standing(
  args: {
    status: string;
    assignments: Assignment[];
    days: Set<string>;
    today: Date;
  },
): Standing {
  const { status, assignments, days, today } = args;

  if (status !== "active") {
    return {
      kind: "notJoined",
      headline: "Invitation not accepted",
      detail: "Nothing you assign will reach them until they join.",
    };
  }

  const visible = assignments.filter((a) => !a.hiddenFromClient);
  if (visible.length === 0) {
    return {
      kind: "nothingAssigned",
      headline: "No training assigned",
      detail: "They have joined and have nothing to do.",
    };
  }

  /* 🔴 THE MISSES ARE NAMED AND BOUNDED, AND BOTH HALVES CAME OUT OF ONE RENDER (2026-09-19).
   *
   * The still showed `1 assigned day not trained` above a week in which NOTHING was missed. The
   * count was CORRECT — traced on his live data: of 68 visible assignments, exactly one past day
   * carries no logged set and is not a task, `Mobility & Handstand Play` on **Sat 29 Aug**, three
   * weeks before the day being rendered. (The two other past-and-unlogged rows are his daily
   * tasks, and `outcome` answers `task` before it ever reads the day.)
   *
   * ⚠️ SO THE DEFECT WAS THE SENTENCE, NOT THE ARITHMETIC — which is exactly the shape that
   * survives, because a coach checking the number finds it right and stops looking. A bare count
   * that disagrees with everything on screen is what teaches somebody to ignore the headline, the
   * same argument the engine's rule 30 makes about a critical nobody can act on.
   *
   * Two changes, and neither is worth having without the other:
   *   • THE HEADLINE NAMES THE SESSION AND THE DAY. `Mobility & Handstand Play · Sat 29 Aug —
   *     not trained` is a sentence a coach can act on; `1` is a number they have to go and
   *     reconcile. His own 2026-09-02 rule, spelled out in plain text rather than as a figure.
   *   • IT IS COUNTED OVER THE LAST FOUR WEEKS. The board's own window — the body-metrics panel
   *     already says "last four weeks" — so the headline and the page agree about what period
   *     they are describing. ⚠️ COST, STATED AND ACCEPTED: a miss ages out of the overview after
   *     four weeks. It is not lost; the Training tab's history still holds it. The alternative is
   *     a headline that shouts for ever about a session nobody can now train.
   */
  const missedRows = visible.filter((a) => {
    if (outcome(a, days, today) !== "missed") return false;
    const key = a.scheduledDateISO === null ? null : dayKey(a.scheduledDateISO);
    if (key === null) return false;
    const age = daysSince(key, today);
    /* ⚠️ `null` MEANS THE DATE DID NOT PARSE, AND THAT IS NOT "inside the window". An unparseable
       date counted as recent would put a row nobody can name into a headline built to name it. */
    return age !== null && age <= missedWindowDays;
  });
  /* Sorted OLDEST FIRST, so the named one is the longest outstanding rather than whichever the
     database happened to return first — the row a coach most needs to ask about. */
  const missedSorted = [...missedRows].sort(
    (a, b) => (a.scheduledDateISO ?? "") < (b.scheduledDateISO ?? "") ? -1 : 1);
  const missed = missedSorted.length;
  const last = lastTrainingDay(days);
  const quietFor = last === null ? null : daysSince(last, today);

  // A missed session is louder than silence, because it names the specific day to ask about.
  if (missed > 0) {
    return {
      kind: "missingWork",
      headline: missedHeadline(missedSorted),
      /* ⚠️ THE SENTENCE IS USED WHOLE, NOT PREFIXED — and the prefix was the defect.
         `describeLastTraining` returns a COMPLETE sentence beginning with its verb ("Trained
         today"), so `Last training ${…toLowerCase()}` rendered "Last training trained today." —
         ungrammatical in every arm it can reach, and in the one place a coach looks first.

         It survived because nothing ran it: `CLAUDE.md`'s recorded shape is that a string
         ASSEMBLED at its use site is the one fallible thing with no runner, and `client.test.ts`
         pinned this arm's `kind` and headline while never reading `detail`. It is pinned now,
         per arm, plus a ban on the double verb that can only come back this way. */
      detail:
        last === null
          ? "Nothing logged against any of them."
          : `${describeLastTraining(last, today)}.`,
    };
  }

  if (quietFor === null || quietFor >= goneQuietAfterDays) {
    return {
      kind: "quiet",
      headline: quietFor === null ? "Nothing logged yet" : `Quiet for ${quietFor} days`,
      detail: "Every assigned day is accounted for, but nothing recent has been logged.",
    };
  }

  return {
    kind: "onTrack",
    headline: describeLastTraining(last, today),
    detail: "Nothing assigned has been missed.",
  };
}

/* ── UNITS FOLLOW THE CLIENT, NEVER THE COACH ──────────────────────────────────────────────
 *
 * `EVERFIT-MAP.md` §20a: units are a PER-CLIENT setting in their product, and it names an open
 * bug in our own list — *"imperial coach's weights stored as kg"*. Storage is always metric;
 * what varies is the reading. **A coach in London opening a client in Texas should see the
 * client's numbers in the client's units**, because the number exists to be said back to that
 * person. Keying it on the viewer would make one athlete's bodyweight change when their coach
 * travelled, which is the same failure `days.ts` refuses for dates.
 *
 * ⚠️ STATED LIMIT, MEASURED: both of this coach's clients read `unit_system = 'metric'`, so the
 * imperial branch is CORRECT AND UNEXERCISED by any real row today. It is written now rather
 * than later because the alternative is a screen that silently states kilograms to somebody who
 * thinks in pounds — but nobody should read a green test here as evidence that an imperial
 * client has been seen.
 */

/** `profiles.unit_system` — 'metric' | 'imperial', and anything unrecognised reads as metric. */
export function isImperial(unitSystem: string | null): boolean {
  return unitSystem === "imperial";
}

/** A stored kilogram figure, said in the client's own units. */
export function formatWeight(kg: number | null, unitSystem: string | null): string | null {
  if (kg === null || !Number.isFinite(kg)) return null;
  if (isImperial(unitSystem)) return `${Math.round(kg * 2.20462)} lb`;
  return `${Math.round(kg * 10) / 10} kg`;
}

/** A stored centimetre figure, said in the client's own units. */
export function formatHeight(cm: number | null, unitSystem: string | null): string | null {
  if (cm === null || !Number.isFinite(cm)) return null;
  if (isImperial(unitSystem)) {
    const totalInches = Math.round(cm / 2.54);
    return `${Math.floor(totalInches / 12)}\u2032 ${totalInches % 12}\u2033`;
  }
  return `${Math.round(cm)} cm`;
}

/* ── THE WIRE → THE MODEL ───────────────────────────────────────────────────────────────────
 *
 * These two live here, and not inline in the screen, for the reason this repo keeps paying for:
 * **a pure rule proven in a pure type is not a property of the app until something pins the
 * WIRE.** The screen's `.map()` calls were the only fallible code on the page with no runner —
 * and one of them has a genuine trap in it, below.
 */

/** A `coach_assignments` row as PostgREST returns it, with the workout name embedded. */
export type AssignmentRow = {
  id: string;
  scheduled_date: string | null;
  hidden_from_client: boolean;
  note: string | null;
  /** W137 (R7) — optional because a read that does not select them is still a valid row; absent reads as 0 / null. */
  day_position?: number | null;
  assigned_at?: string | null;
  /**
   * ⚠️ PostgREST RETURNS AN EMBEDDED RESOURCE AS AN OBJECT *OR* AN ARRAY DEPENDING ON THE
   * RELATIONSHIP IT INFERRED, and the generated types allow both. Narrowing it by hand is not
   * defensiveness: reading `.name` off an array yields `undefined`, so the whole column would
   * render "Untitled workout" with nothing red — a screen full of plausible blanks.
   */
  coach_workouts:
    | { name: string | null; kind?: string | null; task_optional?: boolean | null }
    | { name: string | null; kind?: string | null; task_optional?: boolean | null }[]
    | null;
};

export function toAssignments(rows: AssignmentRow[]): Assignment[] {
  return rows.map((a) => ({
    id: a.id,
    scheduledDateISO: a.scheduled_date,
    workoutName: embeddedName(a.coach_workouts),
    hiddenFromClient: a.hidden_from_client,
    // ONE FUNCTION, NOT A SECOND COPY OF THE TEST — `roster.ts` owns it and both screens ask it.
    isTask: isTaskAssignment(a.coach_workouts as AssignmentEmbed),
    isOptional: isOptionalTask(a.coach_workouts as AssignmentEmbed),
    note: a.note,
    dayPosition: a.day_position ?? 0,
    assignedAtISO: a.assigned_at ?? null,
  }));
}

function embeddedName(embed: AssignmentRow["coach_workouts"]): string | null {
  if (embed === null) return null;
  if (Array.isArray(embed)) return embed[0]?.name ?? null;
  return embed.name;
}

/** A `set_logs` row (W112: `rosterData.SET_DAY_SELECT`, the set and its session's scheduled day), as this page reads it. */
export function toLoggedSets(rows: Parameters<typeof rawSetOf>[0][]): LoggedSet[] {
  return rows.map(rawSetOf).map((s) => ({ userId: s.user_id, completedAtISO: s.completed_at, scheduledDateISO: s.scheduled_date ?? null }));
}
